import { Navigate, Outlet, createRootRoute, createRoute, createRouter } from '@tanstack/react-router';
import { ErrorBanner } from '@/components/ui/ErrorBanner';
import { ConflictsScreen } from '@/features/conflicts/ConflictsScreen';
import { DocumentsScreen } from '@/features/documents/DocumentsScreen';
import { PeriodsScreen } from '@/features/periods/PeriodsScreen';
import { InstructionsScreen } from '@/features/instructions/InstructionsScreen';
import { SettingsScreen } from '@/features/settings/SettingsScreen';
import { useSession } from '@/lib/auth/useSession';
import type { Capability } from '@/lib/auth/capabilities';
import { ForbiddenPage } from './pages/StatusPages';
import { useAuthStore } from '@/store/useAuthStore';
import { InitAuth } from '@/services/authService';
import { CrashPage, NoAccessPage, NotFoundPage, SessionErrorPage, Splash, VerifyDevicePage } from './pages/StatusPages';
import { SignInPage } from './pages/SignInPage';
import { AppShell } from './shell/AppShell';

/**
 *   __root          auth bootstrap + global error banner
 *   ├─ /sign-in     public; bounces home when signed in
 *   └─ _app         signed-in only (AppShell)
 *      ├─ /              → /chat
 *      ├─ /chat          chat.use (/chat/$conversationId: the open conversation)
 *      ├─ /documents     documents.read
 *      ├─ /periods       documents.read (Process: pipeline.run, Close: period.close)
 *      ├─ /conflicts     conflicts.read
 *      ├─ /instructions  instructions.read
 *      └─ /settings      any signed-in user (own profile, password, devices)
 *   Each screen is capability-checked: no read capability → 403 in place.
 */

const rootRoute = createRootRoute({
  component: () => (
    <>
      <InitAuth />
      <ErrorBanner />
      <Outlet />
    </>
  ),
  notFoundComponent: NotFoundPage,
});

function SignInGate() {
  const status = useAuthStore((s) => s.sessionStatus);
  if (status === 'ready' || status === 'mfa') return <Navigate to="/" />;
  if (status === 'restoring') return <Splash />;
  return <SignInPage />;
}

const signInRoute = createRoute({ getParentRoute: () => rootRoute, path: '/sign-in', component: SignInGate });

function AuthGate() {
  const status = useAuthStore((s) => s.sessionStatus);
  switch (status) {
    case 'idle':
      return <Navigate to="/sign-in" />;
    case 'restoring':
      return <Splash />;
    case 'mfa':
      return <VerifyDevicePage />;
    case 'no-access':
      return <NoAccessPage />;
    case 'error':
      return <SessionErrorPage />;
    default:
      return <AppShell />;
  }
}

const appRoute = createRoute({ getParentRoute: () => rootRoute, id: '_app', component: AuthGate });
const indexRoute = createRoute({ getParentRoute: () => appRoute, path: '/', component: () => <Navigate to="/chat" /> });
function gated(cap: Capability, Screen: () => React.ReactNode) {
  return function Gated() {
    const { can } = useSession();
    return can(cap) ? <Screen /> : <ForbiddenPage />;
  };
}

// Chat itself is rendered by AppShell (kept mounted across tabs); the routes only gate it.
// /chat/<sessionId> is the open conversation (features/chat/useChatUrl).
const chatRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/chat',
  component: gated('chat.use', () => null),
});
const chatConversationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/chat/$conversationId',
  component: gated('chat.use', () => null),
});
const documentsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/documents',
  component: gated('documents.read', DocumentsScreen),
});
const periodsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/periods',
  component: gated('documents.read', PeriodsScreen),
});
const conflictsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/conflicts',
  component: gated('conflicts.read', ConflictsScreen),
});
const instructionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/instructions',
  component: gated('instructions.read', InstructionsScreen),
});

const settingsRoute = createRoute({ getParentRoute: () => appRoute, path: '/settings', component: SettingsScreen });

const routeTree = rootRoute.addChildren([
  signInRoute,
  appRoute.addChildren([
    indexRoute,
    chatRoute,
    chatConversationRoute,
    documentsRoute,
    periodsRoute,
    conflictsRoute,
    instructionsRoute,
    settingsRoute,
  ]),
]);

export const router = createRouter({ routeTree, defaultErrorComponent: CrashPage });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
